import { useCallback } from 'react';
import { AutocompleteSuggestion } from './AutocompleteSuggestion';
import { SearchInputWithAutocomplete } from './SearchInputWithAutocomplete';
import { mediaQuery, useMediaQuery } from 'academy-common-lib/utils/Styling';
import ACInlineContentCard from 'academy-common-lib/components/card/ACInlineContentCard';
import { List } from 'foundations-components/transitional/components/List';
// @ts-expect-error Could not find a declaration file for module
import FormattedDuration from 'I18n/components/FormattedDuration';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'academy-common-lib/utils/getLang';
import UsageTracker from '../../../utils/UsageTracker';
import useNavigate from '../../../hooks/useNavigate';
import { RESULTS_URL_FRAGMENT } from '../../../constants/Navigation';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
// @ts-expect-error Could not find a declaration file for module
import { useLocationWithoutParams } from '../../../hooks/searchParamHooks';
import { useHistory, useLocation } from 'react-router-dom';
import { getPlaylistUrl, getRelativeOverviewUrl } from '../../../utils/RoutingUtils';
import { useSearchWithAutocompleteContext } from './SearchInputWithAutocompleteProvider';
import { getShortContentUrl } from '../../shortContent/shortContentRouting';
import { Text } from 'foundations-components/components/Text';
import styled, { css } from 'foundations-theming/utils';
import FormattedList from 'I18n/components/FormattedList';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MainSearchWithAutocompleteInputWrapper = styled.div.withConfig({
  displayName: "MainSearchWithAutocompleteInput__MainSearchWithAutocompleteInputWrapper",
  componentId: "sc-1roqyww-0"
})(["ul[role='listbox']{border:1px solid ", ";max-height:640px;ul{border:none;> *:hover{background-color:", ";}}}", ";"], ({
  theme
}) => theme.color['border-core-subtle'], ({
  theme
}) => theme.color['fill-surface-recessed'], ({
  theme
}) => mediaQuery('notMobile', css(["input[type='search']{height:58px;font-size:20px;padding:", ";padding-right:", ";}svg[data-icon-name='Search']{block-size:30px !important;inline-size:30px !important;padding-top:", ";padding-right:", ";}svg[data-icon-name='Remove']{block-size:20px !important;inline-size:20px !important;}"], theme.space['500'], theme.space['1400'], theme.space['100'], theme.space['100'])));
const MainSearchAutocompleteRow = ({
  option
}) => {
  const {
    searchValue,
    setSelectedItem,
    setHideResults
  } = useSearchWithAutocompleteContext();
  const {
    term,
    text,
    index: indexPopularTerm,
    noSuggestions
  } = option;
  const {
    resourceThumbnail,
    resourceDuration,
    numberOfLessons,
    numberOfItems,
    resourceName,
    resourceType,
    language,
    certificate,
    index: indexQuickLink,
    resourceId
  } = option;
  const location = useLocation();
  const handleOnSuggestionClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'main-search-autocomplete-selected',
      name: term,
      search: searchValue,
      language: getLang(),
      position: indexPopularTerm + 1,
      whichButton: 'click',
      appearsIn: 'suggestions'
    });
    setSelectedItem(option);
    setHideResults(true);
  }, [indexPopularTerm, option, searchValue, setHideResults, setSelectedItem, term]);
  const handleOnContentClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'main-search-autocomplete-selected',
      name: resourceName,
      search: searchValue,
      contentType: resourceType,
      language,
      isCertification: certificate,
      position: indexQuickLink + 1,
      whichButton: 'click',
      appearsIn: 'content'
    });
  }, [certificate, indexQuickLink, language, resourceName, resourceType, searchValue]);
  if (term) return /*#__PURE__*/_jsx(AutocompleteSuggestion, {
    label: text,
    noSuggestions: !!noSuggestions,
    render: content => /*#__PURE__*/_jsx(UIRouterLink, {
      onClick: handleOnSuggestionClick,
      to: `${RESULTS_URL_FRAGMENT}?search=${encodeURIComponent(term)}`,
      use: "unstyled",
      children: content
    }),
    searchValue: searchValue,
    text: term
  });
  if (!resourceId) return null;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(View, {
      paddingBlock: "200",
      paddingInline: "500",
      children: /*#__PURE__*/_jsx(ACInlineContentCard, {
        duration: resourceDuration,
        footer: /*#__PURE__*/_jsxs(List, {
          inline: true,
          use: "inline-divided",
          wrapChildren: true,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: certificate ? 'learningCenter.awards.CERTIFICATE' : `learningCenter.resources.type.${resourceType}`
          }), /*#__PURE__*/_jsx(FormattedList, {
            exclusive: true,
            children: [!!numberOfItems && /*#__PURE__*/_jsx(FormattedMessage, {
              "data-test-id": "playlist-total-items",
              message: "learningCenter.savedItems.playlistDetails.numberOfPlaylistItems",
              options: {
                count: numberOfItems
              }
            }), !!numberOfLessons && /*#__PURE__*/_jsx(FormattedMessage, {
              "data-test-id": "track-total-lessons",
              message: "learningCenter.tracks.readout.lessons",
              options: {
                count: numberOfLessons
              }
            }), !!resourceDuration && /*#__PURE__*/_jsx(FormattedDuration, {
              format: "medium",
              from: +new Date() - resourceDuration * 1000
            })].filter(Boolean)
          })]
        }),
        name: resourceName,
        onClick: handleOnContentClick,
        thumbnailSrc: resourceThumbnail,
        to: resourceType === 'SHORT_FORM_CONTENT' ? getShortContentUrl({
          shortId: resourceId,
          search: searchValue
        }) : resourceType === 'LEARNING_PATH' ? getPlaylistUrl(resourceId) : getRelativeOverviewUrl(location, resourceId, resourceType, language),
        use: "no-hover"
      })
    })
  );
};

// this component handles data from the SearchWithAutocomplete context to handle custom UX and events for the main search
export const MainSearchWithAutocompleteInput = () => {
  const {
    searchValue
  } = useSearchWithAutocompleteContext();
  const {
    isTouchscreen
  } = useMediaQuery();
  const navigate = useNavigate();
  const [hasSearchParam, cleanSearchPath] = useLocationWithoutParams(['search']);
  const location = useLocation();
  const history = useHistory();
  const openSearchResults = useCallback(term => navigate(`${RESULTS_URL_FRAGMENT}?search=${encodeURIComponent(term)}`), [navigate]);
  const handleOnSearchEnter = useCallback(currentSearchValue => openSearchResults(currentSearchValue), [openSearchResults]);
  const handleOnItemEnter = useCallback(({
    item
  }) => {
    const {
      term,
      index: popularTermIndex
    } = item;
    const {
      resourceUrl,
      resourceName,
      language,
      resourceType,
      certificate,
      resourceId,
      index: quickLinkIndex
    } = item;
    if (resourceUrl) {
      UsageTracker.track('interaction', {
        action: 'main-search-autocomplete-selected',
        name: resourceName,
        search: searchValue,
        contentType: resourceType,
        language,
        isCertification: certificate,
        position: quickLinkIndex + 1,
        whichButton: 'enter',
        appearsIn: 'content'
      });
      const url = resourceType === 'LEARNING_PATH' ? getPlaylistUrl(resourceId) : getRelativeOverviewUrl(location, resourceId, resourceType, language);
      history.push(url);
    } else if (term) {
      UsageTracker.track('interaction', {
        action: 'main-search-autocomplete-selected',
        name: term,
        search: searchValue,
        language: getLang(),
        position: popularTermIndex + 1,
        whichButton: 'enter',
        appearsIn: 'suggestions'
      });
      openSearchResults(term);
    }
  }, [history, location, openSearchResults, searchValue]);
  const handleOnClear = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'main-search-autocomplete-clear',
      search: searchValue
    });
    if (hasSearchParam) navigate(cleanSearchPath, {
      method: 'replace'
    });
  }, [cleanSearchPath, hasSearchParam, navigate, searchValue]);

  // callback for SearchInputWithAutocomplete to update the search on item selection (keyboard or click) if possible (only popular terms)
  const getSelectedItemSearchValue = item => {
    const {
      term
    } = item;
    return term || null;
  };
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(MainSearchWithAutocompleteInputWrapper, {
      children: /*#__PURE__*/_jsx(Text, {
        color: "text-core-default",
        textAlign: "left",
        children: /*#__PURE__*/_jsx(SearchInputWithAutocomplete, {
          autofocus: !isTouchscreen,
          autoselectFirst: true,
          "data-test-id": "filter-type-search",
          getSelectedItemSearchValue: getSelectedItemSearchValue,
          itemComponent: MainSearchAutocompleteRow,
          onClear: handleOnClear,
          onSearchEnter: handleOnSearchEnter,
          onSelectionEnter: handleOnItemEnter
        })
      })
    })
  );
};